import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type Ref,
} from "react";
import { usePosterTheme } from "@/components/shapes/html-frame/html-poster-theme";
import { prefersReducedMotion } from "@/lib/reveal-motion";
import { WORKSPACE_PANE_ATTR } from "@/lib/workspace-pane";
import { useWallMeasure } from "./use-wall-measure";
import { useWallViewport, useWallViewportSize } from "./use-wall-viewport";
import type { WallNewItem } from "./use-wall-new-items";
import type { ContextMenuAnchor } from "@/components/ui/context-menu";
import { WallCard } from "./wall-card";
import {
  estimateWallCardHeight,
  estimateWallNewCardHeight,
  sameNewCardEstimates,
} from "./wall-card-height";
import { wallFrame } from "./wall-frame-source";
import { WallNewCard } from "./wall-new-card";
import {
  reconcileWallLayout,
  wallGeometry,
  wallCssVars,
  WALL_HEADER_HEIGHT,
  type WallLayout,
} from "./wall-layout";
import { WallScrollRootContext } from "./wall-scroll-root";
import {
  notifyWallLayout,
  registerWallSurface,
  wallCardFullyInView,
} from "./wall-view-adapter";

export interface WallMasonryHandle {
  /** Scroll a laid-out card to the middle of the view; false when it is not. */
  scrollToCard: (id: string) => boolean;
  /**
   * Give a laid-out card keyboard focus, first scrolling it to the middle
   * of the view unless it already shows whole; false when it is not laid out.
   */
  focusCard: (id: string) => boolean;
  /** Whether the wall is scrolled away from its top. */
  scrolledFromTop: () => boolean;
  scrollToTop: () => void;
}

const NO_IDS: ReadonlySet<string> = new Set();
const NO_NEW_ITEMS: ReadonlyMap<string, WallNewItem> = new Map();
/** The space between cards is empty workspace (`lib/workspace-pane.ts`). */
const WORKSPACE_PANE = { [WORKSPACE_PANE_ATTR]: "true" };
/** About a second of frames for a scrolled-to card to mount. */
const FOCUS_CARD_FRAMES = 60;

function scrollBehavior(): ScrollBehavior {
  return prefersReducedMotion() ? "auto" : "smooth";
}

/** The mounted card button for `id`, if the masonry renders it. */
function mountedCard(
  canvas: HTMLElement | null,
  id: string,
): HTMLElement | null {
  for (const element of canvas?.querySelectorAll<HTMLElement>(
    "[data-wall-card-id]",
  ) ?? []) {
    if (element.dataset.wallCardId === id) {
      return element.querySelector<HTMLElement>("button");
    }
  }
  return null;
}

export interface WallMasonryProps {
  /** The cards, in feed order: frame ids and `newItems`' keys. */
  ids: readonly string[];
  /** New tasks and things among the frames, by key. */
  newItems?: ReadonlyMap<string, WallNewItem>;
  /** Content above the cards (the sticky header, then Pinned). */
  header?: ReactNode;
  /** Content below the cards (empty state, end of the feed). */
  footer?: ReactNode;
  /** Cards pulsing their ring once (a reveal, new work). */
  flashingIds?: ReadonlySet<string>;
  /** New work rising into place. */
  risingIds?: ReadonlySet<string>;
  openedId?: string | null;
  /** The wall scrolled (or laid its cards out again). */
  onScroll?: () => void;
  /** `ids` are the project feed without a search. */
  projectFeed?: boolean;
  /** Back to the project feed without a search (the surface's `showTop`). */
  onShowTop?: () => void;
  onOpen: (id: string) => void;
  onMenu: (id: string, anchor: ContextMenuAnchor) => void;
  ref?: Ref<WallMasonryHandle>;
}

/**
 * `newItems` as the layout reads them: the previous map while every card
 * would be estimated alike, so a new task's status change does not lay the
 * wall out again; a card added or gone does.
 */
function useNewItemEstimates(
  newItems: ReadonlyMap<string, WallNewItem>,
): ReadonlyMap<string, WallNewItem> {
  const [estimates, setEstimates] = useState(newItems);
  const same =
    estimates === newItems || sameNewCardEstimates(estimates, newItems);
  if (!same) setEstimates(newItems);
  return same ? estimates : newItems;
}

/**
 * The wall: one native scroll container with a virtualized masonry of
 * absolutely placed cards (`translate3d`). Only the cards in and near the
 * viewport are mounted, within a per-column budget.
 */
export function WallMasonry({
  ids,
  newItems = NO_NEW_ITEMS,
  header,
  footer,
  flashingIds = NO_IDS,
  risingIds = NO_IDS,
  openedId = null,
  onScroll,
  projectFeed = false,
  onShowTop,
  onOpen,
  onMenu,
  ref,
}: WallMasonryProps) {
  const showTopRef = useRef(onShowTop);
  useLayoutEffect(() => {
    showTopRef.current = onShowTop;
  }, [onShowTop]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const previousRef = useRef<WallLayout | null>(null);
  /** Whether `previousRef` lays out the project feed without a search. */
  const projectFeedRef = useRef(projectFeed);
  const size = useWallViewportSize(scrollRef);
  const geometry = useMemo(() => wallGeometry(size.width), [size.width]);
  const bucket = String(geometry.widthBucket);
  const theme = usePosterTheme();
  const { heights, refFor, measureMounted, prune } = useWallMeasure(bucket);
  const estimates = useNewItemEstimates(newItems);

  useEffect(() => {
    // No cards (a feed starting over) keeps the heights
    // for their return; a feed with cards forgets the ones it left.
    if (ids.length === 0) return;
    prune(new Set(ids));
  }, [ids, prune]);

  const layout = useMemo(() => {
    if (size.width === 0) return null;
    return reconcileWallLayout(
      previousRef.current,
      ids,
      geometry,
      (id) => {
        const measured = heights.get(id, bucket);
        if (measured !== undefined) return measured;
        const item = estimates.get(id);
        return item
          ? estimateWallNewCardHeight(
              item,
              geometry.columnWidth,
              geometry.fontSize,
            )
          : estimateWallCardHeight(
              wallFrame(id),
              geometry.columnWidth,
              geometry.fontSize,
              theme,
            );
      },
      (id) => heights.get(id, bucket) !== undefined,
    );
  }, [bucket, estimates, geometry, heights, ids, size.width, theme]);

  useLayoutEffect(() => {
    previousRef.current = layout;
    projectFeedRef.current = projectFeed;
    notifyWallLayout();
  }, [projectFeed, layout]);

  const mounted = useWallViewport(
    scrollRef,
    canvasRef,
    layout,
    size.height,
    onScroll,
  );

  // Cards mounted, or widened, in this commit are placed by their measured
  // heights before the wall paints.
  useLayoutEffect(measureMounted, [bucket, measureMounted, mounted]);

  /** Centre a laid-out card in the view; false when it is not laid out. */
  const scrollToCard = useCallback((id: string) => {
    const box = previousRef.current?.boxes.get(id);
    const scroller = scrollRef.current;
    const canvas = canvasRef.current;
    if (!box || !scroller || !canvas) return false;
    const centre = canvas.offsetTop + box.y + box.height / 2;
    scroller.scrollTo({
      // The middle of the view below the sticky header.
      top: Math.max(
        0,
        centre - (WALL_HEADER_HEIGHT + scroller.clientHeight) / 2,
      ),
      behavior: scrollBehavior(),
    });
    return true;
  }, []);

  // The workspace view adapter reads card rects from this layout model.
  useLayoutEffect(
    () =>
      registerWallSurface({
        layout: () => previousRef.current,
        scroller: () => scrollRef.current,
        canvas: () => canvasRef.current,
        scrollToCard,
        showsProjectFeed: () => projectFeedRef.current,
        showTop: () => {
          showTopRef.current?.();
          if (scrollRef.current) scrollRef.current.scrollTop = 0;
        },
      }),
    [scrollToCard],
  );

  useImperativeHandle(
    ref,
    () => ({
      scrollToCard,
      focusCard: (id) => {
        if (!wallCardFullyInView(id) && !scrollToCard(id)) return false;
        // A virtualized-away card mounts once the scroll reaches it.
        let frames = FOCUS_CARD_FRAMES;
        const focusWhenMounted = () => {
          const card = mountedCard(canvasRef.current, id);
          if (card) card.focus({ preventScroll: true });
          else if (--frames > 0) requestAnimationFrame(focusWhenMounted);
        };
        focusWhenMounted();
        return true;
      },
      scrolledFromTop: () => (scrollRef.current?.scrollTop ?? 0) > 0,
      scrollToTop: () =>
        scrollRef.current?.scrollTo({ top: 0, behavior: scrollBehavior() }),
    }),
    [scrollToCard],
  );
  // Content above and below the cards (Pinned, the states) sits on the
  // same centred column and type scale; keyboard focus scrolls cards clear
  // of the sticky header.
  const scrollerStyle = useMemo(
    (): CSSProperties => ({
      ...wallCssVars(geometry),
      scrollPaddingTop: WALL_HEADER_HEIGHT,
    }),
    [geometry],
  );

  return (
    <div
      ref={scrollRef}
      className="scrollbar-none relative h-full overflow-y-auto overflow-x-hidden overscroll-contain [overflow-anchor:none]"
      style={scrollerStyle}
      {...WORKSPACE_PANE}
      data-testid="wall-scroll"
      data-columns={geometry.columns}
    >
      <WallScrollRootContext value={scrollRef}>
        {header}
        <div
          ref={canvasRef}
          className="relative"
          style={{ height: layout?.height ?? 0 }}
          {...WORKSPACE_PANE}
          data-testid="wall-masonry"
        >
          {layout &&
            mounted.map((id) => {
              const box = layout.boxes.get(id);
              if (!box) return null;
              const item = newItems.get(id);
              return (
                <div
                  key={id}
                  ref={refFor(id)}
                  className="absolute left-0 top-0"
                  data-wall-card-id={id}
                  style={{
                    width: box.width,
                    transform: `translate3d(${box.x}px, ${box.y}px, 0)`,
                  }}
                >
                  {item ? (
                    <WallNewCard item={item} />
                  ) : (
                    <WallCard
                      id={id}
                      flashing={flashingIds.has(id)}
                      rising={risingIds.has(id)}
                      opened={id === openedId}
                      onOpen={onOpen}
                      onMenu={onMenu}
                    />
                  )}
                </div>
              );
            })}
        </div>
        {footer}
      </WallScrollRootContext>
    </div>
  );
}
